import { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';

import { useSYS5020_R01Store } from './Store_SYS5020_R01';
import type { SYS5020_R01Filter } from './Store_SYS5020_R01';
import {
    downloadExcel_SYS5020_R01,
    getSYS5020_R01AllCount,
    getSYS5020_R01List,
} from './Api_SYS5020_R01';
import { SYS5020_R01Dto } from './Dto_SYS5020_R01';

interface Props {
    gridRef: React.RefObject<AgGridReact<SYS5020_R01Dto> | null>;
    tabList: Tab[] | undefined;
    setMessage: (message: string) => void;
}

export const Content_SYS5020_R01 = (props: Props) => {
    const { gridRef: _gridRef, tabList: _tabList, setMessage } = props;

    const {
        SYS5020_R01Filter,
        SYS5020_R01PastFilter,
        setSYS5020_R01PastFilter,
        setSYS5020_R01List,
        setAllCount,
        initData,
        setScrollTop,
    } = useSYS5020_R01Store();

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    useFetchActionbarAuth('SYS5020_R01');

    const getParams = (filter: SYS5020_R01Filter): Partial<SYS5020_R01Filter> => ({
        dateRange: filter.dateRange,
        userId: filter.userId,
        programId: filter.programId,
        actionType: filter.actionType,
    });

    const renewalAllCount = useCallback(async () => {
        try {
            const res = await getSYS5020_R01AllCount(getParams(SYS5020_R01Filter));
            setAllCount(res.data.data);
            return res.data.data;
        } catch {
            showError('프로그램 로그 조회 실패. (총 ${res.data.data.length}/${total}건)');
            return 0;
        }
    }, [SYS5020_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setLoadingSpinner(true);
        setScrollTop(null);
        try {
            const total = await renewalAllCount();
            const res = await getSYS5020_R01List('search', getParams(SYS5020_R01Filter));
            if (res.data.successFlag) {
                setSYS5020_R01List(res.data.data);
                setSYS5020_R01PastFilter(SYS5020_R01Filter);
                ShowSuccessFetch(setMessage, `프로그램 로그 조회 성공. (총 ${res.data.data.length}/${total}건)`);
            } else {
                showError('프로그램 로그 조회 실패. (총 ${res.data.data.length}/${total}건)');
            }
        } catch {
            showError('프로그램 로그 조회 실패. (총 ${res.data.data.length}/${total}건)');
        } finally {
            setLoadingSpinner(false);
        }
    }, [SYS5020_R01Filter, setSYS5020_R01List, setSYS5020_R01PastFilter, renewalAllCount, setLoadingSpinner, setMessage, setScrollTop]);

    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const res = await downloadExcel_SYS5020_R01('search', getParams(SYS5020_R01PastFilter));
            if (res.data.successFlag) {
                downloadSettingExcel({ responseData: res.data.data, fileName: '프로그램 로그.xlsx' });
                ShowSuccessFetch(setMessage, '프로그램 로그 다운로드 성공');
            } else {
                showWarning('프로그램 로그 다운로드 실패.');
            }
        } catch {
            showError('프로그램 로그 다운로드 실패.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [SYS5020_R01PastFilter, setLoadingSpinner, setMessage]);

    const initContainerData = useCallback(() => {
        initData();
        setMessage('초기화되었습니다.');
    }, [initData, setMessage]);

    return { fetchData, handleExcelClick, initContainerData };
};

export default Content_SYS5020_R01;
